// Code generated by apic; DO NOT EDIT.
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useMemo, type ChangeEvent } from "react";
import type { McpToolMeta, SchemaMeta } from "./types";
import type { ApicClient } from "../react/client";
import { getApiToken } from "../react/auth";
import { SurfaceSamples } from "./SurfaceSamples";
import {
  mcpCurlSample,
  mcpGoSample,
  mcpPythonSample,
  mcpTsSample,
} from "./codegen/mcp";

/** Maximum response body characters (UTF-16 code units) shown in the panel (4 096 chars). */
const TRUNCATE_CHARS = 4096;

/** Truncate a string to TRUNCATE_CHARS characters (UTF-16 code units) and append a note when cut. */
function truncate(s: string): string {
  if (s.length <= TRUNCATE_CHARS) {
    return s;
  }
  return s.slice(0, TRUNCATE_CHARS) + "\n… (truncated at 4 096 chars)";
}

/**
 * MCP JSON-RPC request envelope for a tools/call invocation.
 * Exported for unit testing without React rendering.
 *
 * MCP spec (2025-06-18): params.arguments is the named-argument map.
 */
export type ToolCallRequest = {
  jsonrpc: "2.0";
  id: number;
  method: "tools/call";
  params: {
    name: string;
    arguments: Record<string, unknown>;
  };
};

/**
 * buildToolCallRequest constructs a JSON-RPC 2.0 tools/call envelope.
 * The returned object is serialized verbatim — no key conversion is applied.
 * Exported so callers can unit-test the shape independently.
 */
export function buildToolCallRequest(
  name: string,
  args: Record<string, unknown>,
  id = 1,
): ToolCallRequest {
  return {
    jsonrpc: "2.0",
    id,
    method: "tools/call",
    params: {
      name,
      arguments: args,
    },
  };
}

/**
 * MCP CallToolResult content block (MCP spec 2025-06-18).
 * Only text content is produced by the generated MCP tools.
 */
type MCPContent = {
  type: string;
  text?: string;
};

/**
 * MCP CallToolResult: the result wrapper inside the JSON-RPC response.
 * isError=true when the tool ran but returned an error.
 */
type MCPCallToolResult = {
  content?: MCPContent[];
  isError?: boolean;
};

/**
 * MCP JSON-RPC 2.0 response envelope.
 */
type MCPRPCResponse = {
  jsonrpc?: string;
  id?: number;
  result?: MCPCallToolResult;
  error?: { code?: number; message?: string };
};

/**
 * Returns true when the mcpAuthMode string includes JWT support, meaning
 * a Bearer JWT token sent from the browser will be accepted by the server.
 */
function mcpSupportsJWT(mode: string): boolean {
  return mode === "jwt" || mode === "jwt+api_key";
}

/**
 * Returns true when the mcpAuthMode string requires api_key ONLY — meaning
 * the browser cannot invoke MCP tools (HMAC signing with nonce+timestamp
 * cannot be performed in the browser).
 */
function mcpIsAPIKeyOnly(mode: string): boolean {
  return mode === "api_key";
}

/**
 * McpPanel renders an interactive explorer for the MCP tools surface.
 *
 * Props:
 *   tools        – the list of MCP tools from ServiceMetadata.mcp.
 *   client       – a ApicClient provided by the parent via useExplorerClient.
 *   schemas      – optional schema registry (metadata.schemas) used to render
 *                  schema-driven per-field argument forms. When the tool's
 *                  paramsSchema resolves to a SchemaMeta, the panel renders one
 *                  labeled input per top-level field instead of the freeform JSON
 *                  textarea. Falls back to the freeform textarea when the schema
 *                  name is empty or not found in schemas.
 *   mcpAuthMode  – auth mode string from ServiceMetadata.mcpAuthMode:
 *                  "jwt" | "api_key" | "jwt+api_key" | "none".
 *                  When "api_key" (HMAC-only), the Invoke button is disabled and
 *                  a clear note explains that api_key/HMAC auth cannot be
 *                  performed from the browser (nonce+timestamp+HMAC signing is
 *                  required; use the CLI/SDK instead).
 *                  When JWT is accepted ("jwt" | "jwt+api_key"), the existing
 *                  Bearer-token flow is available and the note says so.
 *                  When "none"/public, no token note is shown.
 *   baseUrl      – optional base URL prefix; falls back to client config's baseUrl.
 *   className    – extra CSS class appended to the root element.
 *
 * Code samples (curl | Go | Python | TypeScript) are shown per tool card via
 * SurfaceSamples, reusing mcpAuthMode to select auth headers. When api_key-only,
 * the samples show the HMAC comment instead of an actual header.
 *
 * Auth note: MCP access is authenticated at the server/transport level
 * (per-session, not per-tool). The generated server mounts the MCP HTTP
 * endpoint at /mcp. Each session authenticates once at request time;
 * individual tools do not carry per-tool auth headers.
 *
 * RN-import-safe: no web-global access at module load time.
 */
export function McpPanel(props: {
  tools: McpToolMeta[];
  client: ApicClient;
  schemas?: Record<string, SchemaMeta>;
  mcpAuthMode?: string;
  baseUrl?: string;
  className?: string;
}): JSX.Element {
  const { tools, client, className, baseUrl, schemas, mcpAuthMode = "none" } = props;
  const root = ["apic-ui-mcp-panel", className].filter(Boolean).join(" ");

  const apiKeyOnly = mcpIsAPIKeyOnly(mcpAuthMode);
  const supportsJWT = mcpSupportsJWT(mcpAuthMode);

  return (
    <div className={root}>
      {/* Panel-level auth note: MCP auth is at transport/server level, not per-tool */}
      <div className="apic-ui-mcp-panel-auth-note">
        {apiKeyOnly ? (
          <span className="apic-ui-mcp-panel-auth-note-text apic-ui-mcp-panel-auth-note--apikey">
            {"MCP access requires api_key/HMAC authentication (Authorization: Bearer <hmac-hex> " +
             "plus X-Nonce and X-Timestamp headers). This signing cannot be performed from the " +
             "browser — use the CLI or SDK to invoke MCP tools. The Invoke button is disabled."}
          </span>
        ) : supportsJWT ? (
          <span className="apic-ui-mcp-panel-auth-note-text apic-ui-mcp-panel-auth-note--jwt">
            {"MCP access is authenticated at the server/transport level via Bearer JWT. " +
             "Individual tools do not carry per-tool credentials. " +
             "Supply a Bearer JWT token in the token bar above when your server requires one. " +
             "The MCP HTTP endpoint is served at /mcp."}
          </span>
        ) : (
          <span className="apic-ui-mcp-panel-auth-note-text apic-ui-mcp-panel-auth-note--public">
            {"MCP access is public (no authentication required). " +
             "The MCP HTTP endpoint is served at /mcp."}
          </span>
        )}
      </div>

      {tools.length === 0 && (
        <div className="apic-ui-mcp-panel-empty">
          {"No MCP tools exposed."}
        </div>
      )}
      {tools.map((tool) => (
        <McpToolCard
          key={tool.name}
          tool={tool}
          client={client}
          apiKeyOnly={apiKeyOnly}
          mcpAuthMode={mcpAuthMode}
          {...(schemas !== undefined ? { schemas } : {})}
          {...(baseUrl !== undefined ? { baseUrl } : {})}
        />
      ))}
    </div>
  );
}

/**
 * McpToolCard renders a single MCP tool with its header, argument form,
 * code samples, invoke control, and result display.
 *
 * When the tool's paramsSchema resolves to a known SchemaMeta in the schemas
 * registry, the card renders a per-field labeled input for each top-level field.
 * Secret fields are rendered as empty password-type inputs (never pre-filled).
 * Falls back to the freeform JSON textarea when the schema is not found.
 *
 * When apiKeyOnly is true, the Invoke button is disabled.
 */
function McpToolCard(props: {
  tool: McpToolMeta;
  client: ApicClient;
  schemas?: Record<string, SchemaMeta>;
  apiKeyOnly: boolean;
  mcpAuthMode: string;
  baseUrl?: string;
}): JSX.Element {
  const { tool, client, baseUrl, schemas, apiKeyOnly, mcpAuthMode } = props;

  // Resolve the schema for schema-driven form rendering.
  const resolvedSchema: SchemaMeta | null =
    tool.paramsSchema !== null &&
    tool.paramsSchema !== "" &&
    schemas !== undefined &&
    Object.prototype.hasOwnProperty.call(schemas, tool.paramsSchema)
      ? (schemas[tool.paramsSchema] ?? null)
      : null;

  // Schema-driven form state: one string value per field name.
  const [fieldValues, setFieldValues] = useState<Record<string, string>>(() => {
    if (resolvedSchema === null) return {};
    const init: Record<string, string> = {};
    for (const f of resolvedSchema.fields) {
      init[f.name] = "";
    }
    return init;
  });

  // Freeform textarea state (fallback when schema not resolved).
  const [argsText, setArgsText] = useState<string>("{}");

  const [loading, setLoading] = useState<boolean>(false);
  const [resultText, setResultText] = useState<string | null>(null);
  const [isError, setIsError] = useState<boolean>(false);
  const [statusLabel, setStatusLabel] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Build MCP code samples. useMemo rebuilds only when inputs change.
  // NOTE: the sample context deliberately OMITS the live token so that the
  // generated code snippets use the "<token>" placeholder. This prevents the
  // real bearer token from appearing in copyable <code> DOM elements, defeating
  // TokenBar's password masking. The invoke path (handleInvoke) reads the live
  // token via its own getApiToken() call right before the fetch — so this
  // component never needs to capture the token here. (R2-1 security fix.)
  const resolvedBase = baseUrl !== undefined && baseUrl !== "" ? baseUrl : "https://api.example.com";

  const mcpSamples = useMemo(() => {
    // Compute current args for sample generation.
    let argsObj: Record<string, unknown> = {};
    if (resolvedSchema !== null) {
      for (const f of resolvedSchema.fields) {
        const raw = fieldValues[f.name] ?? "";
        if (raw !== "" && !f.secret) {
          argsObj[f.name] = raw;
        }
      }
    } else if (argsText.trim() !== "" && argsText.trim() !== "{}") {
      try {
        const parsed = JSON.parse(argsText) as unknown;
        if (parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)) {
          argsObj = parsed as Record<string, unknown>;
        }
      } catch {
        // invalid JSON — leave args empty in sample
      }
    }

    // Sample context: baseUrl + args only — NO token/apiKey.
    // Builders fall back to "<token>" placeholders when absent.
    const ctx = {
      baseUrl: resolvedBase,
      ...(Object.keys(argsObj).length > 0 ? { arguments: argsObj } : {}),
    };
    return {
      curl:       mcpCurlSample(tool, ctx, mcpAuthMode),
      go:         mcpGoSample(tool, ctx, mcpAuthMode),
      python:     mcpPythonSample(tool, ctx, mcpAuthMode),
      typescript: mcpTsSample(tool, ctx, mcpAuthMode),
    };
  }, [tool, resolvedBase, mcpAuthMode, fieldValues, argsText, resolvedSchema]);

  async function handleInvoke(): Promise<void> {
    setLoading(true);
    setResultText(null);
    setIsError(false);
    setStatusLabel(null);
    setErrorMsg(null);

    // Build arguments object — either from schema-driven fields or freeform JSON.
    let argsObj: Record<string, unknown> = {};
    if (resolvedSchema !== null) {
      // Schema-driven: assemble from per-field inputs, omitting empty non-required fields.
      for (const f of resolvedSchema.fields) {
        const raw = fieldValues[f.name] ?? "";
        if (raw !== "") {
          argsObj[f.name] = raw;
        }
      }
    } else {
      // Freeform JSON fallback.
      if (argsText.trim() !== "" && argsText.trim() !== "{}") {
        try {
          const parsed = JSON.parse(argsText) as unknown;
          if (parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)) {
            argsObj = parsed as Record<string, unknown>;
          }
        } catch {
          setErrorMsg("Invalid arguments JSON.");
          setLoading(false);
          return;
        }
      }
    }

    // Build JSON-RPC envelope — keys are sent verbatim (no camelCase conversion).
    const envelope = buildToolCallRequest(tool.name, argsObj);
    const bodyStr = JSON.stringify(envelope);

    // Resolve the MCP HTTP endpoint URL. Prefer the prop baseUrl, then fall
    // back to the client's config.baseUrl (accessed via any-cast, same pattern
    // as WebSocketPanel).
    const cfgBaseUrl = (client as any).config != null
      ? ((client as any).config as { baseUrl?: string }).baseUrl
      : undefined;
    const resolvedBaseUrl = (baseUrl !== undefined ? baseUrl : cfgBaseUrl) ?? "";
    const mcpUrl = resolvedBaseUrl.replace(/\/+$/, "") + "/mcp";

    // Build request headers. Inject Bearer token when one is available so the
    // server can authenticate the request. Token is read at call time (not at
    // module load) — RN-safe.
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      "Accept": "application/json",
    };
    const tok = getApiToken();
    if (tok !== null) {
      headers["Authorization"] = "Bearer " + tok;
    }

    try {
      // fetch is a web/Node global; capability-guarded at call time — not
      // accessed at module load so this module is safe to import on React Native.
      if (typeof fetch === "undefined") {
        setErrorMsg("fetch is unavailable in this runtime.");
        setLoading(false);
        return;
      }

      const response = await fetch(mcpUrl, {
        method: "POST",
        headers,
        body: bodyStr,
        credentials: "same-origin",
      });

      const text = await response.text();
      setStatusLabel(String(response.status));

      if (!response.ok) {
        // HTTP error — show the raw body.
        setIsError(true);
        setResultText(truncate(text));
        setLoading(false);
        return;
      }

      // Parse the JSON-RPC 2.0 response envelope.
      let rpc: MCPRPCResponse;
      try {
        rpc = JSON.parse(text) as MCPRPCResponse;
      } catch {
        setIsError(true);
        setResultText(truncate(text));
        setLoading(false);
        return;
      }

      // JSON-RPC protocol error (method not found, parse error, etc.).
      if (rpc.error !== undefined) {
        setIsError(true);
        const msg = rpc.error.message ?? "JSON-RPC error";
        const code = rpc.error.code !== undefined ? " (code " + String(rpc.error.code) + ")" : "";
        setResultText(msg + code);
        setLoading(false);
        return;
      }

      // Successful tools/call: extract content text from the CallToolResult.
      const toolResult = rpc.result;
      if (toolResult === undefined) {
        setResultText("(empty result)");
        setLoading(false);
        return;
      }

      // isError=true means the tool ran but reported failure.
      const toolIsError = toolResult.isError === true;
      setIsError(toolIsError);

      // Collect all content text blocks.
      const content = toolResult.content;
      if (content !== undefined && content.length > 0) {
        const parts = content
          .filter((c): c is { type: string; text: string } => c.type === "text" && c.text !== undefined)
          .map((c) => c.text);
        if (parts.length > 0) {
          setResultText(truncate(parts.join("\n")));
        } else {
          // Non-text content (e.g. image) — show raw JSON.
          try {
            setResultText(truncate(JSON.stringify(toolResult, null, 2)));
          } catch {
            setResultText("(non-text content)");
          }
        }
      } else {
        // No content blocks — pretty-print the whole result.
        try {
          setResultText(truncate(JSON.stringify(toolResult, null, 2)));
        } catch {
          setResultText("(empty content)");
        }
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        setErrorMsg(err.message);
      } else {
        setErrorMsg("An unexpected error occurred.");
      }
    } finally {
      setLoading(false);
    }
  }

  const isSuccess = statusLabel !== null && !isError;
  const invokeDisabled = loading || apiKeyOnly;

  return (
    <div className="apic-ui-mcp-tool-card">
      {/* Header: tool name + optional title */}
      <div className="apic-ui-mcp-tool-header">
        <span className="apic-ui-mcp-tool-name">{tool.name}</span>
        {tool.title !== "" && (
          <span className="apic-ui-mcp-tool-title">{tool.title}</span>
        )}
      </div>

      {/* Description */}
      {tool.description !== "" && (
        <div className="apic-ui-mcp-tool-description">
          <span className="apic-ui-mcp-tool-description-text">{tool.description}</span>
        </div>
      )}

      {/* Schema names */}
      <div className="apic-ui-mcp-tool-schemas">
        {tool.paramsSchema !== null && (
          <span className="apic-ui-mcp-tool-schema-label">
            {"Params: "}
            <code>{tool.paramsSchema}</code>
          </span>
        )}
        {tool.resultSchema !== null && (
          <span className="apic-ui-mcp-tool-schema-label">
            {"Result: "}
            <code>{tool.resultSchema}</code>
          </span>
        )}
      </div>

      {/* Arguments form — schema-driven when schema is known, freeform JSON otherwise */}
      {resolvedSchema !== null ? (
        <div className="apic-ui-mcp-tool-args apic-ui-mcp-tool-args--schema">
          <div className="apic-ui-mcp-tool-args-schema-label">
            {"Arguments"}
          </div>
          {resolvedSchema.fields.map((field) => (
            <div key={field.name} className="apic-ui-mcp-tool-arg-field">
              <label
                className="apic-ui-mcp-tool-arg-field-label"
                htmlFor={"apic-ui-mcp-arg-" + tool.name + "-" + field.name}
              >
                <span className="apic-ui-mcp-tool-arg-field-name">{field.name}</span>
                <span className="apic-ui-mcp-tool-arg-field-type">{" (" + field.tsType + (field.required ? ", required" : ", optional") + ")"}</span>
                {field.secret && (
                  <span className="apic-ui-mcp-tool-arg-field-secret">{" [secret]"}</span>
                )}
              </label>
              <input
                id={"apic-ui-mcp-arg-" + tool.name + "-" + field.name}
                className="apic-ui-mcp-tool-arg-field-input"
                type={field.secret ? "password" : "text"}
                placeholder={field.required ? field.name : ""}
                value={fieldValues[field.name] ?? ""}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const val = e.target.value;
                  setFieldValues((prev) => ({ ...prev, [field.name]: val }));
                }}
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="apic-ui-mcp-tool-args">
          <label
            className="apic-ui-mcp-tool-args-label"
            htmlFor={"apic-ui-mcp-args-" + tool.name}
          >
            {"Arguments (JSON)"}
          </label>
          <textarea
            id={"apic-ui-mcp-args-" + tool.name}
            className="apic-ui-mcp-tool-args-textarea"
            rows={4}
            placeholder={"{}"}
            value={argsText}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => { setArgsText(e.target.value); }}
          />
        </div>
      )}

      {/* Code samples — curl | Go | Python | TypeScript */}
      <SurfaceSamples
        samples={mcpSamples}
        className="apic-ui-mcp-tool-samples"
      />

      {/* Invoke control */}
      <div className="apic-ui-mcp-tool-actions">
        <button
          className={
            "apic-ui-mcp-tool-invoke" +
            (loading ? " apic-ui-mcp-tool-invoke--loading" : "") +
            (apiKeyOnly ? " apic-ui-mcp-tool-invoke--disabled-apikey" : "")
          }
          disabled={invokeDisabled}
          onClick={() => { void handleInvoke(); }}
        >
          {loading ? "Invoking…" : "Invoke"}
        </button>
      </div>

      {/* Error message (argument parse error or fetch error) */}
      {errorMsg !== null && (
        <div className="apic-ui-mcp-tool-error">
          <span className="apic-ui-mcp-tool-error-msg">{errorMsg}</span>
        </div>
      )}

      {/* Result display */}
      {statusLabel !== null && (
        <div className="apic-ui-mcp-tool-result">
          <span
            className={
              "apic-ui-mcp-tool-status" +
              (isSuccess ? " apic-ui-mcp-tool-status--success" : "") +
              (isError ? " apic-ui-mcp-tool-status--error" : "")
            }
          >
            {isError ? "error" : "ok"}
            {" (" + statusLabel + ")"}
          </span>
          {resultText !== null && (
            <pre className="apic-ui-mcp-tool-result-body">
              <code>{resultText}</code>
            </pre>
          )}
        </div>
      )}
    </div>
  );
}
